import { Avatar, Icon } from "@helmr/ui";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { searchLeads, searchMembers } from "../../api/lists";
import { AREAS } from "../nav";
import { useAppContext, useSessionSnapshot } from "../permissions";
import { useServices } from "../services";
import { strings } from "../strings";
import { mobile } from "./strings";
import { handOverSentinel, useSheetBack } from "./useSheetBack";
import s from "./mobile.module.css";

const RECENT_KEY = "helmr.recentSearches";
const t = mobile.search;

/** Per business and staff member: recents are people's names, and a device is shared across logins and tenants. */
export const recentKey = (businessId: string | null, staffId: string | null) => `${RECENT_KEY}:${businessId ?? ""}:${staffId ?? ""}`;

export function readRecent(key: string): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(key) ?? "[]") as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string").slice(0, 5) : [];
  } catch { return []; }
}
function writeRecent(key: string, list: string[]) {
  try { localStorage.setItem(key, JSON.stringify(list.slice(0, 5))); } catch { /* private mode or quota: recents are a nicety */ }
}

/** Bold the matching part. Plain text in, nodes out: no HTML is ever built from the query. */
export function Highlight({ text, query }: { text: string; query: string }) {
  const q = query.trim();
  if (q.length < 1) return <>{text}</>;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => { const id = setTimeout(() => setV(value), ms); return () => clearTimeout(id); }, [value, ms]);
  return v;
}

/** Full-screen search: Recent, Jump to chips, People (same calls as the desktop palette) and Pages. */
export function SearchScreen({ open, onClose, membersOnly }: { open: boolean; onClose: () => void; membersOnly?: boolean }) {
  const { client } = useServices();
  const ctx = useAppContext();
  const sess = useSessionSnapshot();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const storeKey = recentKey(sess.businessId, sess.staffId);
  const [recent, setRecent] = useState<string[]>(() => readRecent(storeKey));
  const input = useRef<HTMLInputElement>(null);
  useSheetBack(open, onClose);
  useEffect(() => { if (open) { setRecent(readRecent(storeKey)); input.current?.focus(); } else setQuery(""); }, [open, storeKey]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const raw = query.trim();
  const q = useDebounced(raw, 200);
  const canMembers = ctx.has("members.view");
  const canLeads = !membersOnly && ctx.has("leads.view");
  const searching = open && q.length >= 2 && q === raw;
  const members = useQuery({ queryKey: ["search", "members", sess.businessId, sess.branchId, q], queryFn: ({ signal }) => searchMembers(client, q, signal), enabled: searching && canMembers, staleTime: 15_000 });
  const leads = useQuery({ queryKey: ["search", "leads", sess.businessId, sess.branchId, q], queryFn: ({ signal }) => searchLeads(client, q, signal), enabled: searching && canLeads, staleTime: 15_000 });

  if (!open) return null;

  const pages = membersOnly ? [] : AREAS.filter((a) => a.allow(ctx.has));
  const matchPages = raw ? pages.filter((a) => a.label.toLowerCase().includes(raw.toLowerCase())) : [];
  const go = (to: string, remember: string) => { writeRecent(storeKey, [remember, ...readRecent(storeKey).filter((r) => r !== remember)]); onClose(); handOverSentinel(); navigate(to, { replace: true }); };

  const person = (id: string, name: string, phone: string | null, to: string) => (
    <button key={id} type="button" className={s.opt} onClick={() => go(to, name)}>
      <Avatar id={id} name={name} size="md" />
      <span className={s.optText}><b><Highlight text={name} query={raw} /></b>{phone ? <small>{phone}</small> : null}</span>
    </button>
  );
  const people = [
    ...(members.data ?? []).map((m) => person(m.id, m.name, m.phone, `/members/${m.id}`)),
    ...(leads.data ?? []).map((l) => person(l.id, l.name ?? l.phone ?? l.id, l.name ? l.phone : null, `/leads/${l.id}`)),
  ];

  return createPortal(
    <div className={s.search} role="dialog" aria-modal="true" aria-label={t.label}>
      <div className={s.srh}>
        <label className={s.sf}>
          <Icon name="search" size={18} />
          <input ref={input} type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={membersOnly ? t.placeholderMembers : t.placeholder} aria-label={t.label} autoComplete="off" enterKeyHint="search" />
        </label>
        <button type="button" className={s.cancel} onClick={onClose}>{t.cancel}</button>
      </div>
      <div className={s.sres}>
        {raw === "" ? (
          <>
            {recent.length > 0 ? (
              <>
                <h3>{t.recent}</h3>
                <div className={s.chips}>
                  {recent.map((r) => <button key={r} type="button" onClick={() => setQuery(r)}>{r}</button>)}
                </div>
              </>
            ) : null}
            {pages.length > 0 ? (
              <>
                <h3>{t.jump}</h3>
                <div className={s.chips}>
                  {pages.filter((a) => a.key !== "home").slice(0, 8).map((a) => <button key={a.key} type="button" onClick={() => go(a.path, a.label)}>{a.label}</button>)}
                </div>
              </>
            ) : null}
          </>
        ) : (
          <>
            {canMembers || canLeads ? (
              <>
                <h3>{t.people}</h3>
                {raw.length < 2 ? <p className={s.hint}>{t.minChars}</p> : (members.isFetching || leads.isFetching || q !== raw) ? (
                  <div className={s.grp} aria-busy="true" role="status"><span className="sr-only">{t.searching}</span>{[0, 1, 2].map((i) => <div key={i} className={s.skel} />)}</div>
                ) : people.length > 0 ? <div className={s.grp}>{people.map((p, i) => <Fragment key={i}>{p}</Fragment>)}</div> : (
                  <p className={s.hint}>{t.none(raw)}</p>
                )}
              </>
            ) : null}
            {matchPages.length > 0 ? (
              <>
                <h3>{t.pages}</h3>
                <div className={s.grp}>
                  {matchPages.map((a) => (
                    <button key={a.key} type="button" className={s.opt} onClick={() => go(a.path, a.label)}>
                      <span className={s.logo}><Icon name={a.icon} size={16} /></span>
                      <span className={s.optText}><b><Highlight text={a.label} query={raw} /></b></span>
                    </button>
                  ))}
                </div>
              </>
            ) : null}
            {!canMembers && !canLeads && matchPages.length === 0 ? <p className={s.hint}>{strings.shell.noResults}</p> : null}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
